import clsx from 'clsx';
import React, { useEffect } from 'react';
import './table.css';

import {
  IconSelector,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';

import { useFormatText } from '@/hooks/useFormatText';

import PageEmptyState from '../EmptyState/PageEmptyState';
import Pagination from '../Pagination';
import SkeletonLoader from '../SkeletonLoader';

interface ICustomTableProps<T extends object> {
  dataSource: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[];
  rowKey?: string;
  total?: number;
  currentPage?: number;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  onChangePagination?: (page: number) => void;
  className?: string;
  maxHeight?: string;
  hidePagination?: boolean;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (id: string) => void;
  showHeader?: boolean;
  emptyImgSrc?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyButtonText?: string;
  emptyButtonIcon?: React.ReactNode;
  onEmptyBtnClick?: () => void;
  isEmptySearch?: boolean;
  isEmpty?: boolean;
  emptyClassName?: string;
  isLoading: boolean;
  fieldName?: string;
  sortDirection?: string;
  lodingClassName?: string;
  rootClassName?: string;
  pageSize?: number;
}

type TColumn = {
  key?: string;
  title?: string;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  sorter?: boolean;
  handleSort?: () => void;
  render?: (record: unknown, index: number) => React.ReactNode;
};

const CustomTable = <T extends object>({
  dataSource,
  columns,
  rowKey = 'id',
  total,
  currentPage = 1,
  setCurrentPage,
  onChangePagination,
  className = '',
  hidePagination = false,
  // maxHeight = 'max-h-[41.844rem]',
  onRowClick,
  showHeader = true,
  emptyImgSrc,
  emptyTitle,
  emptyDescription,
  emptyButtonText,
  isEmpty = false,
  onEmptyBtnClick,
  emptyClassName,
  emptyButtonIcon,
  isLoading = false,
  isEmptySearch = false,
  fieldName,
  sortDirection,
  lodingClassName,
  rootClassName,
  pageSize = 10,
}: ICustomTableProps<T>): React.ReactElement => {
  const { formatText } = useFormatText();

  useEffect(() => {
    if (dataSource.length === 0 && currentPage > 1) {
      setCurrentPage?.((prevPage) => prevPage - 1);
    }
  }, [dataSource, currentPage, setCurrentPage]);

  useEffect(() => {
    if (onChangePagination) onChangePagination(currentPage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  //on page change scroll to top
  useEffect(() => {
    if (hidePagination) return;
    const scrollableContainer = document.querySelector('.main-container');
    if (scrollableContainer) {
      scrollableContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [currentPage, hidePagination]);

  return (
    <div
      className={clsx('w-full', className, {
        'flex items-center justify-center h-full': isEmpty,
      })}
    >
      {!isEmpty && (
        <div className="relative">
          <div
            className={clsx(
              'flex flex-col h-[calc(100vh-442px)] lg:h-[calc(100vh-350px)] border border-neutral-200 rounded-2xl overflow-hidden bg-white relative',
              (isLoading || dataSource?.length === 0) && lodingClassName,
              rootClassName
            )}
          >
            <div className="grow overflow-auto custom-scrollbar">
              <table className="w-full shadow-small text-sm text-start text-gray-500 border-collapse">
                {showHeader && (
                  <thead className="bg-neutral-100 sticky top-0 z-20">
                    <tr>
                      {columns.map((col, index) => (
                        <th
                          key={col.key as string}
                          className={clsx(
                            'px-4 py-3 font-medium text-sm text-neutral-secondary bg-neutral-100',
                            col.sorter ? 'cursor-pointer' : 'cursor-default',
                            index === 0 && 'rounded-tl-lg!',
                            columns.length - 1 === index && 'rounded-tr-lg!',
                            'whitespace-nowrap'
                          )}
                          style={{
                            minWidth: col?.width,
                            textAlign: col?.align,
                          }}
                        >
                          <div
                            className={clsx(
                              'flex items-center',
                              col.align && `justify-${col?.align}`
                            )}
                          >
                            <span onClick={col.handleSort}>
                              {formatText(col.title)}
                            </span>
                            <span onClick={col.handleSort}>
                              {col?.sorter ? (
                                fieldName === col?.key ? (
                                  sortDirection === 'ASC' ? (
                                    <IconChevronUp
                                      size={16}
                                      className="ml-2.5 text-primary-600"
                                    />
                                  ) : sortDirection === 'DESC' ? (
                                    <IconChevronDown
                                      size={16}
                                      className="ml-2.5 text-primary-600"
                                    />
                                  ) : (
                                    <IconSelector
                                      height={20}
                                      width={20}
                                      className="ml-2.5 text-neutral-600"
                                    />
                                  )
                                ) : (
                                  <IconSelector
                                    height={20}
                                    width={20}
                                    className="ml-2.5 text-neutral-600"
                                  />
                                )
                              ) : null}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody className="relative">
                  {isLoading ? (
                    <>
                      {Array.from({ length: 5 }).map((_, sIndex) => (
                        <tr
                          key={`skeleton-${sIndex}`}
                          className="border-b border-neutral-200 bg-shades-white! h-16"
                        >
                          {(columns as TColumn[]).map(
                            (col: TColumn, cIndex: number) => (
                              <td
                                key={col?.key ?? `s-${cIndex}`}
                                className="px-4 py-2.75 text-sm font-medium text-neutral-primary relative"
                                style={{
                                  minWidth: col?.width,
                                  textAlign: col?.align,
                                }}
                              >
                                <SkeletonLoader
                                  className="h-6 w-full"
                                  variant="text"
                                />
                              </td>
                            )
                          )}
                        </tr>
                      ))}
                    </>
                  ) : dataSource?.length > 0 ? (
                    <>
                      {dataSource?.map((record: T, index: number) => (
                        <tr
                          key={`${String(record[rowKey as keyof T])}-${index}`}
                          className={clsx(
                            'border-b border-neutral-200 bg-shades-white! hover:bg-gray-50',
                            (hidePagination &&
                              dataSource.length - 1 ===
                                dataSource.indexOf(record)) ||
                              (!hidePagination &&
                                dataSource.length >= pageSize &&
                                index === dataSource.length - 1)
                              ? 'border-0!'
                              : '',
                            onRowClick && 'cursor-pointer!'
                          )}
                          onClick={() =>
                            onRowClick?.(record[rowKey as keyof T] as string)
                          }
                        >
                          {columns.map((col) => (
                            <td
                              key={col.key as string}
                              className="px-4 py-2.75 text-sm font-medium text-neutral-primary relative"
                              style={{
                                minWidth: col?.width,
                                textAlign: col?.align,
                              }}
                            >
                              {col.render
                                ? col.render(record, index)
                                : record?.[col.key as keyof T]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="relative">
                        {isEmptySearch && (
                          <div className="flex flex-col items-center justify-center py-10 bg-white h-full">
                            <PageEmptyState
                              image={'/assets/svg/not-found.svg'}
                              title={formatText('common.table.no-search-title')}
                              description={formatText(
                                'common.table.no-search-subtitle'
                              )}
                              containerClassName="h-auto!"
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {!hidePagination && (
              <Pagination
                total={total ?? 0}
                currentPage={currentPage}
                pageSize={pageSize}
                setCurrentPage={setCurrentPage!}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      )}

      {isEmpty && (
        <PageEmptyState
          image={emptyImgSrc ?? '/assets/svg/not-found.svg'}
          title={emptyTitle || formatText('common.table.no-data-title')}
          description={
            emptyDescription || formatText('common.table.no-data-subtitle')
          }
          buttonLabel={emptyButtonText}
          buttonIcon={emptyButtonIcon}
          onButtonClick={onEmptyBtnClick}
          containerClassName={emptyClassName}
        />
      )}
    </div>
  );
};

export default CustomTable;
